import type { LucideIcon } from "lucide-react";
import { HeartPulse } from "lucide-react";
import type { Lang } from "./i18n";
import { siteOrigin, type SeoDocument } from "./seoConfig";

export const industrySlugs = [
  "medical-aesthetics",
  "dental-clinics",
  "pet-services",
  "education-training",
  "field-services",
  "hospitality-rentals",
  "fitness-wellness",
  "local-services",
  "work-order-services",
] as const;

export type IndustrySlug = (typeof industrySlugs)[number];

export interface IndustryWorkflow {
  title: string;
  description: string;
  outcome: string;
}

export interface IndustrySolutionCopy {
  name: string;
  shortDescription: string;
  heroTitle: string;
  heroDescription: string;
  challenges: string[];
  workflows: IndustryWorkflow[];
  outcomes: string[];
  integrations: string[];
}

export interface IndustrySolution {
  slug: IndustrySlug;
  icon: LucideIcon;
  copy: Record<Lang, IndustrySolutionCopy>;
}

// Only published solutions belong here: this registry drives routes, links, and prerendering.
export const industrySolutions: IndustrySolution[] = [
  {
    slug: "dental-clinics",
    icon: HeartPulse,
    copy: {
      en: {
        name: "Dental Clinics",
        shortDescription: "Handle new-patient inquiries, appointment scheduling, intake, and recall follow-up.",
        heroTitle: "A dental AI receptionist that moves every request toward a booking",
        heroDescription: "ClawEase captures visit reason, contact details, provider or location preference, timing constraints, and approved non-clinical fields, then prepares the request for scheduling or staff review.",
        challenges: ["Front-desk teams must answer calls while supporting patients in the clinic.", "New-patient, insurance, provider, and scheduling details arrive incomplete.", "Reschedules, recalls, and confirmations create repetitive follow-up work."],
        workflows: [
          { title: "New-patient intake", description: "Collect contact details, visit reason, preferred provider or location, scheduling constraints, and approved insurance fields.", outcome: "A complete intake summary ready for review" },
          { title: "Appointment scheduling", description: "Check availability, offer suitable times, create or update the appointment, and send confirmation through the preferred channel.", outcome: "A booking completed in the same conversation" },
          { title: "Recall and rescheduling", description: "Run approved recall lists, help patients choose a new time, and update appointment status without manual phone tag.", outcome: "Recovered chair time and a cleaner schedule" },
        ],
        outcomes: ["More calls converted into appointments", "Fewer incomplete new-patient records", "Less repetitive confirmation and rescheduling work"],
        integrations: ["Practice management system", "Patient intake forms", "Google Calendar", "CRM", "SMS", "Email"],
      },
      zh: {
        name: "牙科诊所",
        shortDescription: "处理新客咨询、预约排期、初诊资料和复诊召回。",
        heroTitle: "推动每个请求进入预约的牙科 AI 前台",
        heroDescription: "ClawEase 理解患者联系诊所的原因，采集前台需要的非临床信息，查询可预约时段，并更新团队正在使用的预约和客户系统。",
        challenges: ["前台既要接电话，也要服务到店患者。", "新客、保险、医生偏好和排期信息经常不完整。", "改期、复诊召回和确认产生大量重复跟进。"],
        workflows: [
          { title: "新客资料采集", description: "采集联系方式、到诊原因、医生或门店偏好、时间限制及已批准的保险字段。", outcome: "形成可审核的完整初诊摘要" },
          { title: "预约排期", description: "查询空闲时间、推荐合适时段、创建或更新预约，并通过偏好渠道发送确认。", outcome: "在同一次沟通中完成预约" },
          { title: "复诊召回与改期", description: "按照批准的召回名单联系患者、协助选择新时间，并直接更新预约状态。", outcome: "找回诊椅利用率并保持排期整洁" },
        ],
        outcomes: ["让更多来电转化为预约", "减少不完整的新客资料", "降低确认和改期的重复工作"],
        integrations: ["诊所管理系统", "初诊表单", "Google Calendar", "CRM", "短信", "邮件"],
      },
    },
  },
];

export function getIndustrySolution(slug: string | undefined) {
  return industrySolutions.find((industry) => industry.slug === slug);
}

export function industrySolutionPath(slug: IndustrySlug) {
  return `/ai-receptionist/solutions/${slug}`;
}

export function getIndustrySeoDocument(industry: IndustrySolution): SeoDocument {
  const content = industry.copy.en;
  const canonicalPath = industrySolutionPath(industry.slug);
  const title = industry.slug === "dental-clinics"
    ? "ClawEase | Free AI Receptionist for Dental Clinics"
    : `ClawEase | ${content.name} AI Receptionist & Workflow Solution`;
  const description = content.heroDescription;
  const keywords = [
    `${content.name} AI receptionist`,
    `${content.name} workflow automation`,
    `${content.name} customer service`,
    "free receptionist demo",
    "ClawEase",
  ];

  return {
    title,
    description,
    keywords,
    robots: "index,follow",
    canonicalUrl: `${siteOrigin}${canonicalPath}`,
    alternates: [
      { hreflang: "en", href: `${siteOrigin}${canonicalPath}` },
      { hreflang: "x-default", href: `${siteOrigin}${canonicalPath}` },
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          name: title,
          url: `${siteOrigin}${canonicalPath}`,
          description,
          keywords: keywords.join(", "),
          about: { "@type": "SoftwareApplication", name: "ClawEase", applicationCategory: "BusinessApplication" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "ClawEase", item: siteOrigin },
            { "@type": "ListItem", position: 2, name: "Industry Solutions", item: `${siteOrigin}/en` },
            { "@type": "ListItem", position: 3, name: content.name, item: `${siteOrigin}${canonicalPath}` },
          ],
        },
      ],
    },
  };
}
